import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { cn } from '@/lib/utils';
import type { PractitionerNavigationItem } from './navigationItems';
import { PractitionerMobileMoreMenuFooter } from './PractitionerMobileMoreMenuFooter';

interface PractitionerMobileMoreMenuProps {
  items: PractitionerNavigationItem[];
  onClose: () => void;
}

export function PractitionerMobileMoreMenu({ items, onClose }: PractitionerMobileMoreMenuProps) {
  const { t } = useTranslation('onboarding');

  useEffect(
    function closeOnEscape() {
      function handleKeyDown(keyboardEvent: KeyboardEvent) {
        if (keyboardEvent.key === 'Escape') {
          onClose();
        }
      }
      document.addEventListener('keydown', handleKeyDown);
      return function unsubscribe() {
        document.removeEventListener('keydown', handleKeyDown);
      };
    },
    [onClose]
  );

  return (
    <>
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="lg:hidden fixed inset-0 z-30 bg-zooindigo/20"
      />
      <nav
        aria-label={t('nav.moreMenuLabel')}
        className="lg:hidden fixed inset-x-0 bottom-16 z-40 mx-3 rounded-2xl bg-zoowhite border border-zooborder shadow-lg p-2 grid grid-cols-2 gap-1"
      >
        {items.map(function renderItem(item) {
          if (item.route === null) {
            return null;
          }
          return (
            <NavLink
              key={item.id}
              to={item.route}
              end
              onClick={onClose}
              className={function resolveClassName({ isActive }) {
                return cn(
                  'flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium',
                  isActive === true ? 'bg-zooprimary text-zoowhite' : 'text-zooindigo'
                );
              }}
            >
              <Icon name={item.iconName} />
              <span className="truncate">{t(item.labelKey)}</span>
            </NavLink>
          );
        })}
        <PractitionerMobileMoreMenuFooter />
      </nav>
    </>
  );
}
